/**
 * CarouselRender — /render/carousel
 * Still frame of the Social Go / Social Pro roundabout on a transparent background, for artwork exports.
 * Query: rot (deg, ring rotation) · tab (social-go|social-pro) · persp (px) · origin (perspective-origin y %)
 *        shadow (0|1) · ring (scale of the ring, default 1) · w/h (stage size, CSS px)
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { PRODUCTS, videosForSlot, InstagramAccount, ringStyle, ringRadius } from './ProductShowcase';
import './ShowcaseLanding.css';

const API = process.env.REACT_APP_BACKEND_URL;
const q = new URLSearchParams(window.location.search);
const num = (k, d) => { const v = parseFloat(q.get(k)); return Number.isFinite(v) ? v : d; };
const OPTS = {
  rot: num('rot', 0),
  tab: q.get('tab') || 'social-pro',
  persp: num('persp', 1200),
  tilt: num('tilt', 0), // camera elevation in degrees (looks down on the drum)
  dim: num('dim', 0.55), // brightness of the cards at the very back
  shadow: q.get('shadow') !== '0',
  ring: num('ring', 1),
  rad: num('rad', 1), // ring radius multiplier — >1 spreads the cards apart so fewer overlap
  face: num('face', -0.5), // card facing: -0.5 inward (cover-flow), 0 straight at camera, 0.5 outward (drum)
  front: num('front', 0), // >0: only the first N cards fill the front arc (`step` degrees apart), the rest tuck away at the back
  step: num('step', 0),
  variant: q.get('variant') || 'example', // which poster leads: agent-branded examples (default) or intros
  w: num('w', 1500),
  h: num('h', 800),
};

export default function CarouselRender() {
  const [videos, setVideos] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';
    axios.get(`${API}/api/example-videos/public`).then((r) => setVideos(r.data)).catch(() => setVideos({ introductions: [], examples: [] }));
  }, []);

  useEffect(() => {
    if (!videos) return undefined;
    let alive = true;
    const imgs = [...document.querySelectorAll('[data-testid="carousel-render"] img')];
    Promise.all(imgs.map((im) => (im.complete ? Promise.resolve() : new Promise((r) => { im.onload = r; im.onerror = r; }))))
      .then(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, [videos]);

  if (!videos) return null;
  const slotMap = {};
  [...(videos.introductions || []), ...(videos.examples || [])].forEach((v) => { slotMap[v.id] = v; });
  const items = PRODUCTS.filter((p) => p.tiers.includes(OPTS.tab)).map((p) => {
    if (p.type === 'images') return p;
    const intro = videosForSlot(slotMap, p.intro);
    const example = videosForSlot(slotMap, p.example);
    const set = OPTS.variant === 'intro' ? [...intro, ...example] : [...example, ...intro];
    return { ...p, poster: set.find((v) => v.poster_url)?.poster_url || '' };
  }).filter((p) => p.type === 'images' || p.poster);

  const n = items.length;
  const radius = ringRadius(n) * OPTS.rad;
  const frame = { rot: 0, scale: OPTS.ring, z: 0, opacity: 1, scatter: 0, settle: 0 };
  const step = OPTS.step || 360 / n;
  const front = OPTS.front > 0 ? Math.min(OPTS.front, n) : n;
  const thetaOf = (idx) => {
    if (idx < front) return OPTS.rot + idx * step;
    const back = n - front;
    return 180 + (idx - front - (back - 1) / 2) * Math.min(step * 1.6, 360 / Math.max(back, 1));
  };

  return (
    <div
      className="ps ps-render"
      data-testid="carousel-render"
      data-ready={ready ? '1' : '0'}
      style={{ maxWidth: 'none', padding: 0, width: OPTS.w, margin: 0, background: 'transparent' }}
    >
      <div
        className="ps-stage"
        style={{ height: OPTS.h, width: OPTS.w, perspective: OPTS.persp, overflow: 'visible' }}
      >
        <div style={{ position: 'absolute', inset: 0, transformStyle: 'preserve-3d', transform: `rotateX(${-OPTS.tilt}deg)` }}>
          {items.map((item, idx) => {
            const theta = ((thetaOf(idx) % 360) + 360) % 360;
            const signed = theta > 180 ? theta - 360 : theta;
            const depth = (Math.cos((theta * Math.PI) / 180) + 1) / 2;
            const base = ringStyle(0, n, { ...frame, rot: theta }, radius, 280);
            const style = {
              ...base,
              transform: base.transform.replace(/rotateY\([-\d.]+deg\)$/, `rotateY(${(signed * OPTS.face).toFixed(2)}deg)`),
              top: '50%', left: '50%', marginLeft: -140, marginTop: -249,
              opacity: 1,
              filter: `brightness(${(OPTS.dim + (1 - OPTS.dim) * depth).toFixed(3)})`,
              transition: 'none',
            };
            if (!OPTS.shadow) style.boxShadow = 'none';
            return (
              <div key={item.key} className="ps-card" style={style} data-testid={`render-card-${item.key}`}>
                {item.type === 'images'
                  ? <InstagramAccount images={item.images} interactive={false} testKey={item.key} />
                  : <img src={item.poster} alt={item.label} className="ps-video" draggable={false} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
